import {
  WAIT_TOLERANCE_MINUTES,
  FULL_SCORE_WAIT_EXCESS_MINUTES,
  ZERO_SCORE_WAIT_EXCESS_MINUTES,
  LEVEL_EXCESS_CAP_MINUTES,
  MAX_PLAUSIBLE_WAIT_HOURS,
  DEPARTURE_CUTOFF_LABEL,
  PEER_WAIT_MARGIN_MINUTES,
  KITCHEN_LATE_ROUTE_MIN_DAYS,
  KITCHEN_LATE_ROUTE_MIN_DEALERS,
  LEVEL_CUT_LENGTH_DAYS,
  ON_TIME_LABEL,
  DELAYED_LABEL,
  NOT_COUNTED_LABEL
} from "@/utils/dealerWaitThresholds";
import { CURRENT_FORMULA_WEIGHTS } from "@/utils/levelFormulaWeights";
import {
  formatFormulaChangeDay,
  formatLastPreviousFormulaDay,
  sortChargeTiersByMinutes,
  formatAmount
} from "@/utils/dailyMenuLevelHelpTexts";

const MINUTES_PER_HOUR = 60;

const WAIT_EXAMPLE_TIMES = {
  scheduledDeparture: "09:30",
  dealerArrival: "09:20",
  routeStart: "10:16"
};

const minutesOfDay = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

const exampleWaitMinutes = () => {
  const clockStart = Math.max(
    minutesOfDay(WAIT_EXAMPLE_TIMES.dealerArrival),
    minutesOfDay(WAIT_EXAMPLE_TIMES.scheduledDeparture)
  );
  return minutesOfDay(WAIT_EXAMPLE_TIMES.routeStart) - clockStart;
};

const findChargeTier = (chargeTiers, excessMinutes) =>
  sortChargeTiersByMinutes(chargeTiers)
    .filter((tier) => tier.amount > 0 && tier.minMinutes <= excessMinutes)
    .pop();

const describeExampleCharge = (chargeTiers, excessMinutes) => {
  const tier = findChargeTier(chargeTiers, excessMinutes);
  return tier ? ` Esa ruta se cobra ${tier.currency} ${formatAmount(tier.amount)}.` : "";
};

export const buildDealerWaitExplanation = (chargeTiers, changeDate) => {
  const waitMinutes = exampleWaitMinutes();
  const excessMinutes = Math.max(waitMinutes - WAIT_TOLERANCE_MINUTES, 0);
  const { scheduledDeparture, dealerArrival, routeStart } = WAIT_EXAMPLE_TIMES;

  return [
    {
      title: "Desde cuándo",
      paragraphs: [
        `Desde el ${formatFormulaChangeDay(changeDate)} se mide la espera del repartidor en lugar de la tardanza. Hasta el ${formatLastPreviousFormulaDay(changeDate)} regía la tardanza que registraba el supervisor.`
      ]
    },
    {
      title: "Las dos marcas del repartidor",
      paragraphs: [
        "El repartidor marca dos momentos en su app: cuando llega a tu cocina (se valida con su ubicación) y cuando inicia la ruta."
      ]
    },
    {
      title: "Cuándo corre el reloj",
      paragraphs: [
        "Arranca en lo más tarde entre la llegada del repartidor y la hora programada de salida: si el repartidor llega antes, ese tiempo no cuenta.",
        "Para cuando el repartidor marca el inicio de ruta.",
        `Si MV cambia la hora de salida después de las ${DEPARTURE_CUTOFF_LABEL}, se te cuenta desde la hora más tarde.`
      ]
    },
    {
      title: "Margen y exceso",
      paragraphs: [
        `Se descuentan ${WAIT_TOLERANCE_MINUTES} minutos de margen; lo que pasa de ahí es el exceso.`,
        `Ejemplo: la ruta sale a las ${scheduledDeparture}, el repartidor llega a las ${dealerArrival} e inicia ruta a las ${routeStart}. La espera es de ${waitMinutes} minutos y el exceso, de ${excessMinutes} minutos.${describeExampleCharge(chargeTiers, excessMinutes)}`
      ]
    },
    {
      title: "Cobro por ruta",
      paragraphs: ["Lo que se descuenta por cada ruta está en el Cuadro de descuentos."]
    },
    {
      title: "Cómo entra a tu nivel",
      paragraphs: [
        `La espera pesa ${CURRENT_FORMULA_WEIGHTS.dealerWait} % de tu nivel. Con ${FULL_SCORE_WAIT_EXCESS_MINUTES} minutos o menos de exceso promedio suma 3 puntos; desde ${ZERO_SCORE_WAIT_EXCESS_MINUTES} minutos suma 0.`,
        `En el promedio, cada ruta cuenta como máximo ${LEVEL_EXCESS_CAP_MINUTES} minutos. Las esperas de más de ${MAX_PLAUSIBLE_WAIT_HOURS} horas no se cuentan, porque son un error de marca. Si en el periodo no hay marcas, la espera suma 0.`
      ]
    },
    {
      title: "Estados de cada ruta",
      paragraphs: [
        `${ON_TIME_LABEL}: el repartidor esperó ${WAIT_TOLERANCE_MINUTES} minutos o menos, o salió antes de la hora programada.`,
        `${DELAYED_LABEL}: la espera pasó los ${WAIT_TOLERANCE_MINUTES} minutos y cuenta para tu nivel. Si el repartidor sale más de ${PEER_WAIT_MARGIN_MINUTES} minutos después que el promedio de las otras rutas de la misma hora, se te cuenta ese promedio más ${PEER_WAIT_MARGIN_MINUTES} minutos. Si esa ruta salió tarde frente a las otras en ${KITCHEN_LATE_ROUTE_MIN_DAYS} días o más, con ${KITCHEN_LATE_ROUTE_MIN_DEALERS} repartidores distintos o más, dentro del mismo corte de nivel de ${LEVEL_CUT_LENGTH_DAYS} días, se cuenta la espera completa.`,
        `${NOT_COUNTED_LABEL}: la espera no es de la cocina, así que no se cobra ni cuenta para tu nivel. Pasa cuando el repartidor volvió por esta ruta después de salir con otra, cuando no marcó su llegada o el inicio de ruta, cuando MV frenó la ruta o agregó un pedido mientras esperaba, o cuando la espera pasa de ${MAX_PLAUSIBLE_WAIT_HOURS} horas por error de marca.`,
        "Pasa el mouse o toca la etiqueta del estado para ver la explicación de cada ruta."
      ]
    }
  ];
};

export const TOLERANCE_HIGHLIGHT = `Tienes ${WAIT_TOLERANCE_MINUTES} minutos de tolerancia: la espera recién cuenta desde el minuto ${WAIT_TOLERANCE_MINUTES + 1}.`;

const describeMaximumCharge = (chargeTiers) => {
  const highestTier = chargeTiers.reduce((highest, tier) => (!highest || tier.amount > highest.amount ? tier : highest), null);
  return highestTier ? `El cobro tiene un máximo de ${highestTier.currency} ${formatAmount(highestTier.amount)} por ruta y en` : "En";
};

export const buildFrequentQuestions = (chargeTiers) => [
  {
    question: "¿Y si el repartidor llega muy temprano?",
    answer: "El reloj no arranca hasta la hora programada de salida. Lo que espere antes de esa hora no cuenta."
  },
  {
    question: "¿Y si el repartidor llega tarde?",
    answer: "El reloj arranca cuando llega, no a la hora programada. Su tardanza no te cuenta."
  },
  {
    question: "¿Y si tengo los pedidos listos antes de hora?",
    answer: "Si el repartidor inicia la ruta antes de la hora programada, la espera es 0."
  },
  {
    question: "¿Y si el repartidor no marca la llegada o el inicio de ruta?",
    answer:
      "Esa ruta queda \"Sin marcas\": no se cobra y no entra al promedio. Si en toda la quincena no hay ninguna ruta con marcas, la espera suma 0 puntos."
  },
  {
    question: "¿Y si el repartidor marca el inicio de ruta mucho después de salir?",
    answer: `${describeMaximumCharge(chargeTiers)} el nivel cada ruta cuenta como máximo ${LEVEL_EXCESS_CAP_MINUTES} minutos; las esperas de más de ${MAX_PLAUSIBLE_WAIT_HOURS} horas no se cuentan.`
  },
  {
    question: "¿Y si MV frena la ruta?",
    answer:
      "Si la ruta estuvo frenada por MV mientras el repartidor esperaba, esa espera no se cobra ni cuenta para tu nivel."
  },
  {
    question: "¿Y si MV agrega un pedido cuando el repartidor ya está en tu cocina?",
    answer: "Esa espera no se cobra."
  },
  {
    question: "¿Cuándo se descuenta?",
    answer: "En el pago de la semana a la que pertenece la ruta."
  }
];
